// The words for the campaign tree's levels (owner, 2026-10-25): the top level is a Brand
// ("Hyperprints"), everything under it is a Campaign, and campaigns can hold campaigns. The
// code, the API and the database keep their names (`campaigns`, `root_id`, `parent_id`); only
// what people read changes, and it changes here. The naming has no compliance meaning.

/** A level of the tree, as people read it. */
export type CampaignLevel = "brand" | "campaign";

/** Anything that knows its place in the tree: a node (`parent_id`), or its depth. */
export type LevelSource = { parent_id?: string | null } | { depth: number } | null | undefined;

/** The level of a node: a node with no parent is a brand. Unknown reads as a campaign. */
export function levelOf(c: LevelSource): CampaignLevel {
  if (!c) return "campaign";
  if ("depth" in c && typeof c.depth === "number") return c.depth === 0 ? "brand" : "campaign";
  if ("parent_id" in c) return c.parent_id ? "campaign" : "brand";
  return "campaign";
}

/** The level a new node gets under `parent` (null: the top level, a brand). */
export function childLevel(parent: unknown): CampaignLevel {
  return parent ? "campaign" : "brand";
}

const ONE: Record<CampaignLevel, string> = { brand: "brand", campaign: "campaign" };
const MANY: Record<CampaignLevel, string> = { brand: "brands", campaign: "campaigns" };

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "brand" or "campaign"; `many` for the plural, `title` to start a sentence or a label. */
export function levelNoun(
  level: CampaignLevel,
  o: { many?: boolean; title?: boolean } = {},
): string {
  const w = o.many ? MANY[level] : ONE[level];
  return o.title ? cap(w) : w;
}

/** The noun for a node: "brand" for a top-level one, else "campaign". */
export function nounOf(c: LevelSource, o: { many?: boolean; title?: boolean } = {}): string {
  return levelNoun(levelOf(c), o);
}

/** The words that do not depend on a node. */
export const CAMPAIGN_WORDS = {
  /** The Campaigns screen: the sidebar item, its crumb and the tree's heading. One word like
   * every other sidebar item; the tree's top rows are the brands. */
  screen: "Campaigns",
  /** A picker's "every node" choice. */
  all: "All campaigns",
  /** A picker's "nothing" choice. */
  none: "No campaign",
  /** A node of either level, where the level is not known or both are meant. */
  either: "campaign",
} as const;

/** "New brand" at the top level, "New campaign" under a brand or a campaign. */
export function newLabel(parent: unknown): string {
  return `New ${levelNoun(childLevel(parent))}`;
}

/** "Create brand" or "Create campaign", the drawer's primary button. */
export function createLabel(parent: unknown): string {
  return `Create ${levelNoun(childLevel(parent))}`;
}
